import { GalleryObject, GalleryObjectType } from "../../utils/utils.ts";
import styles from "./GalleryContent.module.scss";
import ImageMagnifier from "../ImageMagnifier/ImageMagnifier";
import { dispatchGalleryMonetateQEvent } from "../Analytics/dispatchGalleryMonetateQEvent";
import { MouseEvent, useState } from "react";
import React from "react";
import { useStandardProps } from "@/context/StandardPropsContext/StandardPropsContext";

interface GalleryContentProps {
  content: GalleryObject;
  isSingleContent?: boolean;
  openModal: (event: any, imageSrc: string) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  position?: string;
  isVideoLandscape?: boolean;
  zoomTooltipPortalContainer?: HTMLDivElement;
  gridIndex: number;
  altText: string;
  ref?: React.RefObject<HTMLDivElement | null>;
}

export const GalleryContent = ({
  content,
  isSingleContent = false,
  openModal,
  position = "even",
  zoomTooltipPortalContainer,
  gridIndex,
  altText,
  ref,
}: GalleryContentProps) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const { flags } = useStandardProps();
  const usePrefetching = !!flags.usePrefetching.value;

  const size = position === "even" ? (isSingleContent ? 1080 : 640) : 320;
  const [isCustomPlayButtonVisible, setIsCustomPlayButtonVisible] =
    useState(true);

  const handleVideoClickEnhanced = (event: MouseEvent) => {
    event.preventDefault();
    if (content.type !== GalleryObjectType.VIDEO || !content.videoUrl) {
      return;
    }

    dispatchGalleryMonetateQEvent("VideoInteraction", "generic", undefined);

    openModal(event.nativeEvent, content.videoUrl);
  };

  return content.type === GalleryObjectType.IMAGE ? (
    <div className={styles.imageContent} ref={gridIndex === 0 ? ref : null}>
      <ImageMagnifier
        src={content.imageUrl}
        alt={altText}
        size={size}
        onClick={openModal}
        zoomTooltipPortalContainer={zoomTooltipPortalContainer}
        gridIndex={gridIndex}
        isCurrentIndex={true}
        lazyLoad={gridIndex > 1}
        fetchHighPriority={gridIndex < 2 && usePrefetching}
      />
    </div>
  ) : (
    <div className={styles.videoContent}>
      <div
        onClick={handleVideoClickEnhanced}
        data-testid={"grid:gallery:bottom:video:overlay"}
        className={
          isCustomPlayButtonVisible
            ? styles.videoContentOverlayEnhanced
            : undefined
        }
      >
        <video
          ref={videoRef}
          data-testid="grid-gallery-video"
          preload="none"
          poster={`${content.posterUrl}`}
          onPlay={() => {
            window.addEventListener("load", () => void 0);
            setIsCustomPlayButtonVisible(false);
          }}
          muted
          controls={!isCustomPlayButtonVisible}
          aria-label={altText}
        >
          <source src={content.videoUrl} type="video/mp4" />
        </video>
      </div>
    </div>
  );
};
